<script setup lang="ts">
  import {
    CropperCanvas,
    CropperCrosshair,
    CropperHandle,
    CropperImage,
    CropperSelection,
    CropperShade,
  } from '@cropper/elements'
  import { ref, useTemplateRef } from 'vue'
  import { useAppModal } from '@/components/modal'

  CropperCanvas.$define()
  CropperImage.$define()
  CropperHandle.$define()
  CropperCrosshair.$define()
  CropperSelection.$define()
  CropperShade.$define()

  const props = defineProps<{
    aspectX: number,
    aspectY: number,
    title: string,
  }>()

  const crop = defineModel<CropData | null>('crop', { default: null })
  const preview = defineModel<string | null>('preview', { default: null })
  const fileSrc = ref<string>()
  const file = ref<Blob>()

  const refCropperCanvas = useTemplateRef('refCropperCanvas')
  const refCropperImage = useTemplateRef('refCropperImage')
  const refCropperSelection = useTemplateRef('refCropperSelection')

  const originalImageWidth = ref<number>(0)
  const originalImageHeight = ref<number>(0)

  const saveCrop = async () => {
    const width = refCropperSelection.value!.width / refCropperImage.value!.getBoundingClientRect().width * refCropperImage.value!.$image.naturalWidth
    const canvas = await refCropperSelection.value!.$toCanvas({ width })
    preview.value = canvas.toDataURL('image/png')
    const cropperCanvasRect = refCropperCanvas.value!.getBoundingClientRect()
    const cropperImageRect = refCropperImage.value!.getBoundingClientRect()
    crop.value = {
      x: (refCropperSelection.value!.x - cropperImageRect.x + cropperCanvasRect.x) / cropperImageRect.width * originalImageWidth.value,
      y: (refCropperSelection.value!.y - cropperImageRect.y + cropperCanvasRect.y) / cropperImageRect.height * originalImageHeight.value,
      width,
      height: width / (props.aspectX / props.aspectY),
    }
    close()
  }
  const { modal, open, close } = useAppModal((uploadedFile: Blob) => {
    file.value = uploadedFile
    fileSrc.value = URL.createObjectURL(uploadedFile)
  })

  defineExpose({ open })

  const onTransformChange = () => {
    refCropperSelection.value?.$nextTick(() => {
      if (!refCropperImage.value) return
      const cropperImageRect = refCropperImage.value.getBoundingClientRect()
      constrainSelection({
        x: cropperImageRect.width / 2,
        y: cropperImageRect.height / 2,
        width: cropperImageRect.width,
        height: cropperImageRect.height,
      })
      refCropperSelection.value!.$center()
      originalImageWidth.value = refCropperImage.value.$image.naturalWidth
      originalImageHeight.value = refCropperImage.value.$image.naturalHeight
    })
  }
  const onSelectionChange = (event: CustomEvent) => {
    // Prevent the default behavior of the selection change event
    // to avoid the cropper from applying the selection immediately.
    // We want to apply our own constraints instead.
    event.preventDefault()
    constrainSelection(event.detail)
  }
  const constrainSelection = (selection: { x: number, y: number, width: number, height: number }) => {
    if (!refCropperCanvas.value || !refCropperImage.value || !refCropperSelection.value) {
      return
    }
    const cropperCanvasRect = refCropperCanvas.value.getBoundingClientRect()
    const cropperImageRect = refCropperImage.value.getBoundingClientRect()
    const aspectRatio = props.aspectX / props.aspectY
    // Clamp width to the image bounds using the prop aspect ratio, then derive height from it
    const width = Math.min(selection.width, cropperImageRect.width, cropperImageRect.height * aspectRatio)
    const height = width / aspectRatio
    const minX = cropperImageRect.x - cropperCanvasRect.x
    const minY = cropperImageRect.y - cropperCanvasRect.y
    const maxX = minX + cropperImageRect.width - width
    const maxY = minY + cropperImageRect.height - height
    const x = Math.max(minX, Math.min(selection.x, maxX))
    const y = Math.max(minY, Math.min(selection.y, maxY))
    refCropperSelection.value.x = x
    refCropperSelection.value.y = y
    refCropperSelection.value.width = width
    refCropperSelection.value.height = height
    refCropperSelection.value.$render()
  }
</script>

<template>
  <app-modal
    ref="modal"
    :title="title">
    <div>
      {{ originalImageWidth }}x{{ originalImageHeight }}
    </div>
    <cropper-canvas
      ref="refCropperCanvas"
      scale-step="0.1"
      class="aspect-square bg-black"
      theme-color="rgba(51, 153, 255, 0.5)">
      <cropper-image
        ref="refCropperImage"
        :src="fileSrc"
        alt="Picture"
        initial-fit="contain"
        @transform="onTransformChange" />
      <cropper-shade theme-color="rgba(0, 0, 0, 0.65)" />
      <cropper-handle
        action="select"
        plain
        theme-color="rgba(51, 153, 255, 0.5)" />
      <cropper-selection
        ref="refCropperSelection"
        initial-coverage="1"
        movable
        resizable
        outlined
        zoomable
        :aspect-ratio="aspectX / aspectY"
        @change="onSelectionChange">
        <cropper-crosshair
          centered
          theme-color="rgba(238, 238, 238, 0.5)" />
        <cropper-handle
          action="move"
          theme-color="rgba(255, 255, 255, 0)" />
        <cropper-handle
          action="n-resize"
          theme-color="rgba(51, 153, 255, 0.5)" />
        <cropper-handle
          action="e-resize"
          theme-color="rgba(51, 153, 255, 0.5)" />
        <cropper-handle
          action="s-resize"
          theme-color="rgba(51, 153, 255, 0.5)" />
        <cropper-handle
          action="w-resize"
          theme-color="rgba(51, 153, 255, 0.5)" />
        <cropper-handle
          action="ne-resize"
          theme-color="rgba(51, 153, 255, 0.5)" />
        <cropper-handle
          action="nw-resize"
          theme-color="rgba(51, 153, 255, 0.5)" />
        <cropper-handle
          action="se-resize"
          theme-color="rgba(51, 153, 255, 0.5)" />
        <cropper-handle
          action="sw-resize"
          theme-color="rgba(51, 153, 255, 0.5)" />
      </cropper-selection>
    </cropper-canvas>

    <app-modal-footer>
      <app-button
        class="btn-primary"
        @click="saveCrop">
        Simpan
      </app-button>
      <app-button
        class="btn-gray-outline"
        @click="close">
        Batal
      </app-button>
    </app-modal-footer>
  </app-modal>
</template>

<style scoped>
  @reference "@/assets/styles/main.css";

  .btn-icon {
    @apply shrink-0 p-0! text-xs! size-7! sm:size-9!;
  }
</style>
